import { ProductPricingData } from '../components/ProductPricingBadge';

interface PricingLike {
  map?: number;
  mapIsLocked?: boolean;
  msrp?: number;
  retail?: number;
  instantRebate?: number;
  rebateStartAt?: any;
  rebateEndAt?: any;
  mapStartAt?: any;
  manufacturerPriceAsOf?: any;
  manufacturerPriceEndAt?: any;
  salePrice?: number;
  salePriceStartAt?: any;
  salePriceEndAt?: any;
  tradeCredit?: number;
}

export interface EffectiveMap {
  /** Raw MAP from the catalog / manufacturer sheet. */
  raw: number;
  /** Instant rebate amount tied to the current/next window (0 when none). */
  instantRebate: number;
  /** Advertised price after the instant rebate (MAP − IR), when a window exists. */
  afterIR: number | null;
  windowStatus: 'active' | 'upcoming' | 'expired' | 'none';
  /** The MAP floor in effect right now: MAP − IR while a rebate window is active, raw MAP otherwise. */
  floor: number;
  windowStart?: Date | string;
  windowEnd?: Date | string;
  /** Sheet "price as of" / MAP effective date, when the manufacturer file provided one. */
  mapAsOf?: Date | string;
}

/** Stored MAP is the dealer book / list, not MAP-minus-IR. */
function mapLooksLikeAdvertisedList(storedMap: number, advertisedList: number): boolean {
  if (!(advertisedList > 0) || !(storedMap > 0)) return false;
  const abs = Math.abs(storedMap - advertisedList);
  if (abs <= 0.10) return true;
  return abs / advertisedList <= 0.01;
}

function finiteRebate(value: unknown): number {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * Instant rebate for the window on screen.
 * A loaded manufacturer sheet owns the amount for that week: a blank rebate
 * means this week has none. Catalog `pricing.instantRebate` fills in only
 * when no sheet was loaded.
 */
function rebateAmount(
  pricing: PricingLike | undefined,
  sheet?: { instantRebate?: number | null }
): number {
  if (sheet) return finiteRebate(sheet.instantRebate);
  return finiteRebate(pricing?.instantRebate);
}

/**
 * The product's own manufacturer rebate, when it has not started yet.
 * Its dates stay the file's dates. A later week must not be redrawn onto
 * the current sheet week (Sony ILCE1-B: $0 through Oct 4, $500 from Oct 5).
 */
function upcomingProductRebate(
  pricing: PricingLike | undefined,
  nowMs: number
): { amount: number; start: PricingLike['rebateStartAt']; end: PricingLike['rebateEndAt'] } | null {
  const amount = finiteRebate(pricing?.instantRebate);
  const startMs = toTime(pricing?.rebateStartAt);
  if (!(amount > 0) || startMs == null || Number.isNaN(startMs) || startMs <= nowMs) return null;
  const endMs = toTime(pricing?.rebateEndAt);
  if (Number.isNaN(endMs) || (endMs != null && endMs < nowMs)) return null;
  return { amount, start: pricing?.rebateStartAt, end: pricing?.rebateEndAt };
}

/** Returns null when absent, NaN when present but unparseable (fail safe). */
const toTime = (value: any): number | null => {
  if (value == null || value === '') return null;
  if (typeof value.toDate === 'function') value = value.toDate();
  const seconds = value?._seconds ?? value?.seconds;
  if (typeof seconds === 'number') return seconds * 1000;
  const d = value instanceof Date ? value : new Date(value);
  const t = d.getTime();
  return Number.isFinite(t) ? t : NaN;
};

/**
 * Resolves the MAP that should be represented to users. During an
 * instant-rebate window the advertised floor is MAP − IR (manufacturer
 * sheets carry this directly as sellingPriceAfterIR), so raw MAP alone
 * reads as a floor being violated whenever a rebate is running.
 *
 * `sheet` (the masterPricing row) wins for the week it covers. A blank
 * rebate on that row is zero for today. A later rebate stored on the
 * product is shown as upcoming, with the manufacturer's own dates.
 */
const NON_MAP_PRICE_TYPE = /^(msrp|retail|list|list price|srp|suggested retail|dealer|dealer cost|wholesale|cost)$/i;

/**
 * Sheet MAP wins. A non-MAP sheet that only copied its selling price must not
 * fall through to the same number stamped on the catalog.
 */
function storedMapForSheet(
  pricing: PricingLike | undefined,
  sheet?: ProductPricingData
): number {
  const type = String(sheet?.priceType || '').trim();
  const nonMap = !!type && NON_MAP_PRICE_TYPE.test(type);
  const selling = Number(sheet?.sellingPrice ?? 0);
  const fromSheet = Number(sheet?.mapPrice ?? 0);
  // A non-MAP sheet that copied its selling price into mapPrice is not a MAP.
  const sheetMap =
    fromSheet > 0 && !(nonMap && selling > 0 && Math.abs(fromSheet - selling) < 0.005)
      ? fromSheet
      : 0;
  if (sheetMap > 0) return sheetMap;
  const catalog = Number(pricing?.map ?? 0);
  if (!(catalog > 0)) return 0;
  if (pricing?.mapIsLocked) return catalog;
  if (sheet?.catalogMapIsSynthetic === true) return 0;
  if (!sheet || !nonMap) return catalog;
  if (selling > 0 && Math.abs(catalog - selling) < 0.005) return 0;
  if (fromSheet > 0 && Math.abs(catalog - fromSheet) < 0.005) return 0;
  return catalog;
}

export function getEffectiveMap(
  pricing: PricingLike | undefined,
  sheet?: ProductPricingData,
  nowMs: number = Date.now(),
): EffectiveMap | null {
  const storedMap = storedMapForSheet(pricing, sheet);
  const instantRebate = rebateAmount(pricing, sheet);
  const storedAfterIR = Number(sheet?.sellingPriceAfterIR ?? 0);
  const list = Number(pricing?.msrp ?? 0);
  const retail = Number(pricing?.retail ?? 0);
  const advertisedList = list > 0 ? list : retail;
  // Canon ingest used to store MAP-less-IR as mapPrice, which hid MAP − IR.
  // Panasonic sheets copy list/MAP into the after-IR column too; adding IR
  // then invents a MAP above MSRP ($299.99 + $50 → $349.99 on H-H025K).
  // SmallRig 3848: warehouse MAP $18.90 ≈ site list $18.99, leftover IR $2.84
  // must not invent $21.74. Stay tight (10¢ or 1%) so a $20 MAP-minus-IR
  // gap on a $1,000 body still reconstructs.
  let raw = storedMap;
  const mapEqualsAfterIR =
    instantRebate > 0 &&
    storedAfterIR > 0 &&
    storedMap > 0 &&
    Math.abs(storedMap - storedAfterIR) < 0.02;
  const reconstructed =
    storedAfterIR > 0 && instantRebate > 0
      ? Math.round((storedAfterIR + instantRebate) * 100) / 100
      : 0;
  if (mapEqualsAfterIR) {
    if (!mapLooksLikeAdvertisedList(storedMap, advertisedList)) {
      raw = reconstructed;
    }
  } else if (
    reconstructed > 0 &&
    list > 0 &&
    Math.abs(storedMap - reconstructed) < 0.02 &&
    Math.abs(storedAfterIR - list) < 0.02
  ) {
    // Sheet already has the inflated MAP (after-IR + IR); restore list.
    raw = list;
  }
  if (!(raw > 0)) return null;

  const afterIRFromSheet = Number(
    storedAfterIR > 0
      ? storedAfterIR
      : instantRebate > 0
        ? Math.max(raw - instantRebate, 0)
        : 0
  );
  const afterIRCandidate =
    instantRebate > 0 && afterIRFromSheet >= raw
      ? Math.max(raw - instantRebate, 0)
      : afterIRFromSheet;
  const windowStart = sheet?.effectiveDate ?? pricing?.rebateStartAt;
  const windowEnd = sheet?.endDate ?? pricing?.rebateEndAt;
  const mapAsOf = sheet?.effectiveDate ?? pricing?.mapStartAt ?? pricing?.manufacturerPriceAsOf;

  const sheetStatus = String(sheet?.status || '').toLowerCase();
  const sheetCancelled = sheetStatus === 'cancelled' || sheetStatus === 'deleted';

  let windowStatus: EffectiveMap['windowStatus'];
  if (!(instantRebate > 0) || !(afterIRCandidate > 0) || afterIRCandidate >= raw) {
    windowStatus = 'none';
  } else {
    // Ignore stored sheet status=expired when there is no calendar end —
    // Panasonic lookup can stamp expired while IR is still on the catalog
    // (H-H025K / 30mm Macro). A real end date in the past is expired.
    const startMs = toTime(windowStart);
    const endMs = toTime(windowEnd);
    if (sheetCancelled) {
      windowStatus = 'none';
    } else if (startMs != null && !Number.isNaN(startMs) && startMs > nowMs) {
      windowStatus = 'upcoming';
    } else if (sheetStatus === 'upcoming' && (startMs == null || Number.isNaN(startMs))) {
      windowStatus = 'upcoming';
    } else if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
      windowStatus = 'none';
    } else if (endMs != null && endMs < nowMs) {
      windowStatus = 'expired';
    } else {
      windowStatus = 'active';
    }
  }

  const afterIR =
    windowStatus === 'active' || windowStatus === 'upcoming' ? afterIRCandidate : null;

  // The current manufacturer week has no rebate. Keep today's price at MAP
  // and surface the product's later window on its own dates. A cancelled
  // or deleted sheet row is not a blank week — do not advertise a leftover
  // catalog rebate as upcoming.
  if (sheet && windowStatus === 'none' && !sheetCancelled) {
    const upcoming = upcomingProductRebate(pricing, nowMs);
    if (upcoming) {
      const upcomingAfter = Math.max(raw - upcoming.amount, 0);
      return {
        raw,
        instantRebate: upcoming.amount,
        afterIR: upcomingAfter > 0 && upcomingAfter < raw ? upcomingAfter : null,
        windowStatus: 'upcoming',
        floor: raw,
        windowStart: upcoming.start,
        windowEnd: upcoming.end,
        ...(mapAsOf ? { mapAsOf } : {}),
      };
    }
  }

  return {
    raw,
    instantRebate,
    afterIR,
    windowStatus,
    floor: windowStatus === 'active' && afterIR != null ? afterIR : raw,
    windowStart,
    windowEnd,
    ...(mapAsOf ? { mapAsOf } : {}),
  };
}

function positiveAmount(value: unknown): number | null {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * A catalog `pricing.map` copied from retail is not a MAP. The ProMaster API
 * leaves `source.rawData.map` empty when the row had none, and a warehouse
 * stamp with no `map` says the same when the stored number is the advertised
 * retail. A manufacturer file still owns the stored map.
 */
function pricingWithoutSyntheticMap(product: {
  pricing?: PricingLike & {
    warehouseFallback?: { map?: number | null; retail?: number | null } | null;
  };
  manualFields?: Record<string, unknown> | null;
  source?: { rawData?: { map?: unknown } | null } | string | null;
}): PricingLike | undefined {
  const pricing = product.pricing;
  if (!pricing) return pricing;
  // Same order as catalogMapAmount: a locked map, then a manufacturer file,
  // then a warehouse MAP (which can differ from the stored number).
  const locks = product.manualFields;
  if (pricing.mapIsLocked || (locks && typeof locks === 'object' && locks['pricing.map'])) return { ...pricing, mapIsLocked: true };
  if (pricing.manufacturerPriceAsOf) return pricing;
  const stamp = pricing.warehouseFallback;
  const warehouseMap = stamp && typeof stamp === 'object' ? positiveAmount(stamp.map) : null;
  if (warehouseMap != null) return { ...pricing, map: warehouseMap };
  const map = positiveAmount(pricing.map);
  if (map == null) return pricing;
  const raw = product.source && typeof product.source === 'object' ? product.source.rawData : undefined;
  if (raw && typeof raw === 'object' && 'map' in raw && positiveAmount(raw.map) == null) {
    return { ...pricing, map: undefined };
  }
  if (stamp && typeof stamp === 'object' && positiveAmount(stamp.map) == null) {
    const advertised = positiveAmount(stamp.retail) ?? positiveAmount(pricing.retail);
    if (advertised != null && Math.abs(map - advertised) < 0.005) {
      return { ...pricing, map: undefined };
    }
  }
  return pricing;
}

/**
 * The MAP to show for a product. Retail and MSRP are not a MAP: a ProMaster
 * row with only a retail price has no MAP constraint (MOT-839).
 */
export function getEffectiveMapForProduct(
  product: {
    pricing?: PricingLike & {
      warehouseFallback?: { map?: number | null; retail?: number | null } | null;
    };
    pricingData?: ProductPricingData;
    price?: number;
    brand?: string;
    vendorId?: string;
    vendorName?: string;
    manualFields?: Record<string, unknown> | null;
    source?: { rawData?: { map?: unknown } | null } | string | null;
    id?: string;
  },
  nowMs: number = Date.now(),
): EffectiveMap | null {
  return getEffectiveMap(pricingWithoutSyntheticMap(product), product.pricingData, nowMs);
}

/**
 * Rebate validity range for captions, e.g. "from Aug 30, 2026 to Sep 5, 2026".
 * Omits missing ends/starts rather than inventing dates.
 */
export function rebateWindowCaption(
  em: Pick<EffectiveMap, 'windowStatus' | 'windowStart' | 'windowEnd'>,
  formatDate: (value: unknown) => string | null
): string | null {
  if (em.windowStatus !== 'active' && em.windowStatus !== 'upcoming') return null;
  const start = em.windowStart ? formatDate(em.windowStart) : null;
  const end = em.windowEnd ? formatDate(em.windowEnd) : null;
  if (start && end) return `from ${start} to ${end}`;
  if (start) return `from ${start}`;
  if (end) return `ends ${end}`;
  return null;
}

function datedWindowIsActive(startValue: unknown, endValue: unknown, nowMs: number): boolean {
  const endMs = toTime(endValue);
  if (endMs == null || Number.isNaN(endMs) || endMs < nowMs) return false;
  const startMs = toTime(startValue);
  if (startMs != null && !Number.isNaN(startMs) && startMs > nowMs) return false;
  return true;
}

/**
 * True when a rebate or sale has an end date and that window covers now.
 * MAP as-of (no promo end) does not count.
 */
export function isDatedPromoActive(
  pricing: PricingLike | undefined,
  nowMs: number = Date.now(),
  sheet?: Pick<ProductPricingData, 'status' | 'endDate' | 'effectiveDate' | 'instantRebate'>
): boolean {
  const salePrice = Number(pricing?.salePrice ?? 0);
  if (salePrice > 0 && datedWindowIsActive(pricing?.salePriceStartAt, pricing?.salePriceEndAt, nowMs)) {
    return true;
  }

  const amount = rebateAmount(pricing, sheet);
  const rebateStart = sheet ? sheet.effectiveDate : pricing?.rebateStartAt;
  const rebateEnd = sheet ? sheet.endDate : pricing?.rebateEndAt;
  return amount > 0 && datedWindowIsActive(rebateStart, rebateEnd, nowMs);
}

/**
 * Scrape last-checked is only skipped while a dated rebate/sale is active.
 * MAP as-of uses the usual 7-day freshness window.
 */
export function isManufacturerScrapeStale(
  pricing: PricingLike | undefined,
  lastChecked: unknown,
  nowMs: number = Date.now(),
  staleAfterDays = 7,
  sheet?: Pick<ProductPricingData, 'status' | 'endDate' | 'effectiveDate' | 'instantRebate'>
): boolean {
  if (isDatedPromoActive(pricing, nowMs, sheet)) return false;
  const checkedMs = toTime(lastChecked);
  if (checkedMs == null || Number.isNaN(checkedMs)) return false;
  const days = Math.floor((nowMs - checkedMs) / (1000 * 60 * 60 * 24));
  return days > staleAfterDays;
}

export interface DisplaySale {
  isSaleActive: boolean;
  sellingPrice: number;
  regularPrice: number;
  savingsAmount: number;
}

/**
 * Catalog list price with a scrape leftover `retail` above both MAP and MSRP
 * ignored (Promaster HGX: $183 / $290 / $410 retail against a $109.95–$319.95
 * MAP+MSRP book). Mirrors the backend `catalogRegularPrice`: the replacement is
 * the greater of MAP and MSRP, so a corrupt MSRP below MAP never wins.
 */
export function catalogRegularFromPricing(pricing?: PricingLike): number {
  const positive = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  const retail = positive(pricing?.retail);
  const msrp = positive(pricing?.msrp);
  const map = positive(pricing?.map);
  if (retail > 0 && msrp > 0 && map > 0 && retail > msrp && retail > map) {
    return Math.max(msrp, map);
  }
  return retail || msrp || map || 0;
}

/**
 * Sale presentation for product cards. Catalog salePrice wins when cheaper;
 * an active MAP − instant rebate below the list price also counts as a sale
 * (Z 8: $4,299.95 MAP − $700 IR → Save $700.00).
 */
export function resolveDisplaySale(
  product: {
    price?: number;
    isSaleActive?: boolean;
    regularPrice?: number;
    savingsAmount?: number;
    pricing?: PricingLike;
  },
  sheet?: ProductPricingData,
  nowMs: number = Date.now(),
): DisplaySale {
  const rawSelling = Number(product.price ?? 0);
  const leftoverRetail = Number(product.pricing?.retail ?? 0);
  const listFromPricing = catalogRegularFromPricing(product.pricing);
  // Only the leftover retail itself is replaced: a store override priced above
  // MAP/MSRP is a deliberate price and is shown as is.
  const catalogSelling =
    rawSelling > 0 && listFromPricing > 0 && listFromPricing < leftoverRetail && Math.abs(rawSelling - leftoverRetail) < 0.005
      ? listFromPricing
      : rawSelling;
  const catalogRegular = Number(product.regularPrice ?? 0);
  const catalogSavings = Number(product.savingsAmount ?? 0);
  const catalogSale =
    product.isSaleActive === true &&
    catalogSelling > 0 &&
    catalogRegular > catalogSelling;

  const em = getEffectiveMap(product.pricing, sheet, nowMs);
  const afterIR = em?.windowStatus === 'active' ? em.afterIR : null;
  const mapList = Number(em?.raw ?? 0);

  if (afterIR != null && afterIR > 0) {
    const list = Math.max(catalogRegular, catalogSelling, mapList);
    if (list - afterIR >= 1) {
      const selling = catalogSale ? Math.min(catalogSelling, afterIR) : afterIR;
      if (selling < list) {
        return {
          isSaleActive: true,
          sellingPrice: selling,
          regularPrice: list,
          savingsAmount: list - selling,
        };
      }
    }
  }

  if (catalogSale) {
    return {
      isSaleActive: true,
      sellingPrice: catalogSelling,
      regularPrice: catalogRegular,
      savingsAmount: catalogSavings > 0 ? catalogSavings : catalogRegular - catalogSelling,
    };
  }

  return {
    isSaleActive: false,
    sellingPrice: catalogSelling,
    regularPrice: 0,
    savingsAmount: 0,
  };
}

export type LinkedCatalogPriceSource = 'store' | 'map' | 'catalog';

/**
 * Price for one linked catalog row. The store breakdown is what sync writes.
 * MAP (after rebate while that window is active) is the figure on the product
 * card. Catalog `price` is last: a kit page can keep the body's starting price.
 */
export function linkedCatalogListedPrice(
  product: { price?: number; pricing?: PricingLike },
  sheet?: ProductPricingData,
  storeEffectivePrice?: number | null,
): { amount: number | null; source: LinkedCatalogPriceSource } {
  const store = Number(storeEffectivePrice);
  if (Number.isFinite(store) && store > 0) return { amount: store, source: 'store' };
  const floor = getEffectiveMap(product.pricing, sheet)?.floor ?? 0;
  if (floor > 0) return { amount: floor, source: 'map' };
  const catalog = Number(product.price);
  if (Number.isFinite(catalog) && catalog > 0) return { amount: catalog, source: 'catalog' };
  return { amount: null, source: 'catalog' };
}

export interface RebateDisplay {
  show: boolean;
  instantRebate: number;
  tradeCredit: number;
  afterIR: number | null;
  /** Present when the IR window is live or scheduled. */
  windowStatus?: 'active' | 'upcoming';
  startDate?: Date | string;
  endDate?: Date | string;
}

/**
 * Instant-rebate banner. Uses the manufacturer sheet when present
 * and falls back to catalog `pricing.instantRebate` so a missing sheet lookup
 * (search vs brand list) does not hide a current or upcoming promo.
 * "After rebate" price is only for an active window so we do not present a
 * future floor as the current selling price.
 *
 * `tradeCredit` is manufacturer dealer reimbursement (Nikon "Dealer
 * Reimbursement Credit"), not a customer trade-in. It is returned for store
 * internals and does not drive the green promo banner.
 */
export function resolveRebateDisplay(
  pricing: PricingLike | undefined,
  sheet?: ProductPricingData,
  nowMs: number = Date.now(),
): RebateDisplay {
  const em = getEffectiveMap(pricing, sheet, nowMs);
  const windowStatus: RebateDisplay['windowStatus'] =
    em?.windowStatus === 'active' || em?.windowStatus === 'upcoming' ? em.windowStatus : undefined;
  const advertised = Boolean(windowStatus);
  const live = windowStatus === 'active';
  const instantRebate = advertised && em ? em.instantRebate : 0;
  // Prefer the live sheet's Credit Amount. Catalog tradeCredit is often a
  // leftover from a body/MPN row stamped onto a kit (Canon 1483C002 → 1483C010)
  // and must not show as a current dealer program without a live rebate.
  const sheetCredit = Number(sheet?.tradeCredit ?? 0);
  const catalogCredit = Number(pricing?.tradeCredit ?? 0);
  // Catalog credit belongs to the product's rebate window. Don't show it
  // during a manufacturer week that has no rebate of its own.
  const tradeCredit = sheetCredit > 0 ? sheetCredit : live ? catalogCredit : 0;
  // The rebate caption uses the rebate's own window. The sheet's MAP dates
  // are a different week when today's row has no rebate and the product
  // carries a later manufacturer window.
  const startDate = em?.windowStart ?? sheet?.effectiveDate ?? pricing?.rebateStartAt;
  const endDate = em?.windowEnd ?? sheet?.endDate ?? pricing?.rebateEndAt;
  return {
    show: instantRebate > 0,
    instantRebate,
    tradeCredit,
    afterIR: live && em ? em.afterIR : null,
    ...(windowStatus ? { windowStatus } : {}),
    ...(startDate ? { startDate } : {}),
    ...(endDate ? { endDate } : {}),
  };
}
